import React, { useEffect, useRef, useCallback } from 'react';
import { createChart, ColorType } from 'lightweight-charts';

/**
 * 주식 캔들 차트 컴포넌트
 * stock-insight의 Chart.tsx를 React로 포팅
 */
const StockChart = ({
  candles = [],
  showMA5 = true,
  showMA20 = true,
  showMA60 = false,
  height = 400,
}) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const ma5SeriesRef = useRef(null);
  const ma20SeriesRef = useRef(null);
  const ma60SeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);

  // 한국 증권 색상 (상승=빨강, 하락=파랑)
  const colors = {
    background: '#ffffff',
    text: '#191f28',
    grid: '#f1f3f5',
    upColor: '#f04452',      // 상승 - 빨강
    downColor: '#3182f6',    // 하락 - 파랑
    ma5: '#f59e0b',          // 5일선 - 주황
    ma20: '#10b981',         // 20일선 - 초록
    ma60: '#8b5cf6',         // 60일선 - 보라
    volume: '#94a3b8',       // 거래량 - 회색
  };

  // 반응형 차트 높이 계산
  const getChartHeight = useCallback(() => {
    if (typeof window === 'undefined') return height;
    if (window.innerWidth < 768) {
      return Math.min(Math.max(window.innerHeight * 0.35, 250), 350);
    }
    return height;
  }, [height]);

  // 이동평균 계산
  const calculateMA = useCallback((data, period) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push(null);
      } else {
        let sum = 0;
        for (let j = 0; j < period; j++) {
          sum += data[i - j].close;
        }
        result.push(sum / period);
      }
    }
    return result;
  }, []);

  // 차트 초기화
  useEffect(() => {
    if (!chartContainerRef.current || candles.length === 0) return;

    // 기존 차트 제거
    if (chartRef.current) {
      chartRef.current.remove();
    }

    // 차트 생성
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.background },
        textColor: colors.text,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      width: chartContainerRef.current.clientWidth,
      height: getChartHeight(),
      timeScale: {
        timeVisible: false,
        borderColor: colors.grid,
      },
      rightPriceScale: {
        borderColor: colors.grid,
      },
      crosshair: {
        mode: 1,
      },
    });

    chartRef.current = chart;

    // 캔들스틱 시리즈
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: colors.upColor,
      downColor: colors.downColor,
      borderVisible: false,
      wickUpColor: colors.upColor,
      wickDownColor: colors.downColor,
    });
    candlestickSeriesRef.current = candlestickSeries;

    // 캔들 데이터 설정
    const candleData = candles.map((candle) => ({
      time: candle.date,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));
    candlestickSeries.setData(candleData);

    // 거래량 시리즈 (하단)
    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });
    volumeSeriesRef.current = volumeSeries;

    const volumeData = candles.map((candle) => ({
      time: candle.date,
      value: candle.volume,
      color: candle.close >= candle.open
        ? 'rgba(240, 68, 82, 0.3)'
        : 'rgba(49, 130, 246, 0.3)',
    }));
    volumeSeries.setData(volumeData);

    // 이동평균선 시리즈
    ma5SeriesRef.current = chart.addLineSeries({
      color: colors.ma5,
      lineWidth: 1,
      visible: showMA5,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    ma20SeriesRef.current = chart.addLineSeries({
      color: colors.ma20,
      lineWidth: 1,
      visible: showMA20,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    ma60SeriesRef.current = chart.addLineSeries({
      color: colors.ma60,
      lineWidth: 1,
      visible: showMA60,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    // 이동평균 데이터 설정
    const ma5Values = calculateMA(candles, 5);
    const ma20Values = calculateMA(candles, 20);
    const ma60Values = calculateMA(candles, 60);

    ma5SeriesRef.current.setData(
      candles.map((c, i) => ({
        time: c.date,
        value: ma5Values[i],
      })).filter(d => d.value !== null)
    );

    ma20SeriesRef.current.setData(
      candles.map((c, i) => ({
        time: c.date,
        value: ma20Values[i],
      })).filter(d => d.value !== null)
    );

    ma60SeriesRef.current.setData(
      candles.map((c, i) => ({
        time: c.date,
        value: ma60Values[i],
      })).filter(d => d.value !== null)
    );

    // 차트 fit
    chart.timeScale().fitContent();

    // 반응형 처리
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: getChartHeight(),
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [candles, colors, getChartHeight, calculateMA, showMA5, showMA20, showMA60]);

  // 이동평균선 표시/숨김 업데이트
  useEffect(() => {
    ma5SeriesRef.current?.applyOptions({ visible: showMA5 });
  }, [showMA5]);

  useEffect(() => {
    ma20SeriesRef.current?.applyOptions({ visible: showMA20 });
  }, [showMA20]);

  useEffect(() => {
    ma60SeriesRef.current?.applyOptions({ visible: showMA60 });
  }, [showMA60]);

  if (candles.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-2xl py-16 px-5 text-center text-gray-400">
        <p>차트 데이터가 없습니다</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-2xl p-4 mb-4 md:p-3 md:rounded-xl">
      <div className="flex gap-4 mb-3 pl-2 md:gap-3 md:mb-2">
        <span className="text-xs font-medium flex items-center gap-1 before:content-[''] before:inline-block before:w-3 before:h-0.5 before:rounded-sm before:bg-amber-500">MA5</span>
        <span className="text-xs font-medium flex items-center gap-1 before:content-[''] before:inline-block before:w-3 before:h-0.5 before:rounded-sm before:bg-emerald-500">MA20</span>
        <span className="text-xs font-medium flex items-center gap-1 before:content-[''] before:inline-block before:w-3 before:h-0.5 before:rounded-sm before:bg-violet-500">MA60</span>
      </div>
      <div ref={chartContainerRef} className="w-full" />
    </div>
  );
};

export default StockChart;
